<template>
  <Header/>
  <LoginStatus />
  <div class="container is-max-desktop">
    <MessageError />

    <h1 v-if="isOngoing">Forlæng booking #{{ bookingId }}</h1>
    <h1 v-else>Ændr booking #{{ bookingId }}</h1>

    <div v-if="loadError" class="message is-danger">
      <div class="message-body">{{ loadError }}</div>
    </div>

    <div v-else-if="booking.id" class="booking-form">
      <div class="booking-steps-bar">
        <ol class="booking-steps" aria-label="Redigeringstrin">
          <li class="booking-step" :class="stepClass(1)">
            <span class="booking-step__num">1</span>
            <span>Tidsrum</span>
          </li>
          <li class="booking-step" :class="stepClass(2)">
            <span class="booking-step__num">2</span>
            <span>Køretøj</span>
          </li>
          <li class="booking-step" :class="stepClass(3)">
            <span class="booking-step__num">3</span>
            <span>{{ isOngoing ? 'Forlæng' : 'Gem' }}</span>
          </li>
        </ol>
      </div>

      <article v-if="isOngoing" class="message is-info">
        <div class="message-body">
          Du kan kun forlænge bookingen. Kort afslutning sker via «Afslut booking nu» på booking-siden.
        </div>
      </article>

      <div class="form-section booking-period">
        <h2>Tidsrum</h2>
        <div class="booking-period-fields">
          <div class="field">
            <div class="control">
              <label for="start">Afhentnings-tidspunkt</label>
              <input
                class="input"
                type="datetime-local"
                id="start"
                :min="form.minStart"
                :disabled="isOngoing"
                v-model="form.start"
                @change="onDateFieldChange('start')"
              >
            </div>
          </div>
          <div class="field">
            <div class="control">
              <label for="end">Afleverings-tidspunkt</label>
              <input
                class="input"
                type="datetime-local"
                id="end"
                :min="form.minEnd"
                v-model="form.end"
                @change="onDateFieldChange('end')"
              >
            </div>
          </div>
        </div>

        <article v-if="Object.keys(form.errorMessages).length" class="message is-danger">
          <div class="message-body">
            <ul>
              <li v-for="(error, propertyName) in form.errorMessages" :key="propertyName">{{ error }}</li>
            </ul>
          </div>
        </article>

        <article v-if="vehicleConflict" class="message is-warning">
          <div class="message-body">
            Bilen er booket af andre i det valgte tidsrum. Vælg et tidligere sluttidspunkt{{ isOngoing ? '' : ' eller et andet køretøj' }}.
          </div>
        </article>
      </div>

      <div class="form-section available-vehicles">
        <h2>Køretøj</h2>
        <div v-if="isOngoing" class="booking-locked">
          <p>
            {{ booking.vehicle.name }} ({{ booking.vehicle.brand }} {{ booking.vehicle.model }})
          </p>
        </div>
        <ul v-else class="vehicle-list">
          <li
            v-for="(vehicle, vehicleId) in form.availableVehicles"
            :key="vehicleId"
            class="vehicle-option"
            :class="{
              'is-available': vehicle.available,
              'is-unavailable': !vehicle.available,
              'is-selected': form.selectedVehicle == vehicleId && vehicle.available
            }"
          >
            <label class="vehicle-option__label">
              <input
                type="radio"
                name="vehicle"
                :disabled="!vehicle.available"
                :checked="form.selectedVehicle == vehicleId && vehicle.available"
                :value="vehicleId"
                @change="onVehicleChange($event)"
              >
              <span>{{ vehicle.name }} ({{ vehicle.brand }})</span>
              <span v-if="!vehicle.available" class="vehicle-option__status">Ikke ledig</span>
            </label>
          </li>
        </ul>
      </div>

      <div class="form-section finalize-booking">
        <h2>{{ isOngoing ? 'Forlæng booking' : 'Gem ændringer' }}</h2>
        <div class="buttons booking-actions">
          <button class="button is-light" type="button" @click="goBack">Fortryd</button>
          <button
            class="button is-success"
            type="button"
            :disabled="!canSubmit"
            @click="bookingUpdate()"
          >
            {{ submitting ? 'Gemmer…' : (isOngoing ? 'Forlæng booking' : 'Gem ændringer') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <Footer/>
</template>

<script>
import ax from 'axios'
export const axios = ax

import { defineComponent } from 'vue'
import { useComposition } from '@/compositions/composition'

import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import LoginStatus from '@/components/LoginStatus.vue'
import MessageError from '@/components/MessageError.vue'

const minBookingTime = 60 // Minutes
const maxBookingTime = 60 * 24 * 10 // Minutes
const minBookingTimeSteps = 15 // Minutes

export default defineComponent({
  setup() {
    const { global } = useComposition()
    return { global }
  },
  data() {
    return {
      booking: {},
      loadError: '',
      submitting: false,
      form: {
        start: '',
        end: '',
        minStart: '',
        minEnd: '',
        selectedVehicle: 0,
        availableVehicles: {},
        errorMessages: {}
      }
    }
  },
  components: {
    Header,
    Footer,
    LoginStatus,
    MessageError
  },
  created() {
    this.bookingLoad()
  },
  computed: {
    bookingId() {
      return this.$route.params.id
    },
    isOngoing() {
      return this.booking.phase === 10 || this.booking.phase === 11
    },
    nowIsoLocal() {
      return new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 16)
    },
    selectedVehicleAvailability() {
      if (!this.form.selectedVehicle) {
        return null
      }
      return this.form.availableVehicles[this.form.selectedVehicle] || null
    },
    vehicleConflict() {
      if (!this.form.selectedVehicle || Object.keys(this.form.errorMessages).length) {
        return false
      }
      const vehicle = this.selectedVehicleAvailability
      return !!(vehicle && !vehicle.available)
    },
    canSubmit() {
      if (this.submitting || !this.form.selectedVehicle) {
        return false
      }
      if (Object.keys(this.form.errorMessages).length) {
        return false
      }
      const vehicle = this.selectedVehicleAvailability
      if (!vehicle || !vehicle.available) {
        return false
      }
      if (this.isOngoing) {
        return this.form.end > this.toLocalInput(this.booking.end)
      }
      return true
    },
    currentStep() {
      if (this.canSubmit) {
        return 3
      }
      if (!Object.keys(this.form.errorMessages).length) {
        return 2
      }
      return 1
    }
  },
  methods: {
    stepClass(step) {
      const current = this.currentStep
      if (step < current) {
        return 'is-done'
      }
      if (step === current) {
        return 'is-active'
      }
      return ''
    },
    toLocalInput(dbDate) {
      if (!dbDate) {
        return ''
      }
      return String(dbDate).replace(' ', 'T').substr(0, 16)
    },
    goBack() {
      this.$router.push('/booking/' + this.bookingId)
    },
    bookingLoad() {
      axios.get(process.env.VUE_APP_API_ROOT + '/booking.php?id=' + this.bookingId)
        .then((response) => {
          const data = response.data
          if (!data || !data.id) {
            this.loadError = 'Bookingen blev ikke fundet.'
            return
          }
          if (this.global.user.account != data.createdBy.account) {
            this.loadError = 'Du kan kun ændre din husstands bookinger.'
            return
          }
          const phase = parseInt(data.phase, 10)
          if (phase !== 20 && phase !== 10 && phase !== 11) {
            this.loadError = 'Denne booking kan ikke ændres.'
            return
          }
          if ((phase === 10 || phase === 11) && data.endOriginal && data.endOriginal !== '0000-00-00 00:00:00') {
            this.loadError = 'Bookingen er allerede afsluttet før tid.'
            return
          }

          this.booking = data
          document.title = (phase === 20 ? 'Ændr' : 'Forlæng') + ' booking #' + data.id

          const start = this.toLocalInput(data.start)
          const end = this.toLocalInput(data.end)
          this.form.start = start
          this.form.end = end
          this.form.selectedVehicle = parseInt(data.vehicle.id, 10)

          if (phase === 20) {
            this.form.minStart = this.ceilTime(this.nowIsoLocal, minBookingTimeSteps)
            this.form.minEnd = this.dateAdd(start, minBookingTime)
          } else {
            this.form.minStart = start
            this.form.minEnd = this.dateAdd(end, minBookingTimeSteps)
            // Prefill with next possible extension slot
            this.form.end = this.form.minEnd
          }

          this.validatePeriod()
          this.vehicleAvailability(this.form.start, this.form.end)
        })
        .catch(() => {
          this.loadError = 'Kunne ikke hente bookingen.'
        })
    },
    onDateFieldChange(field) {
      if (field === 'start' && !this.isOngoing) {
        this.form.start = this.ceilTime(this.form.start, minBookingTimeSteps)
        if (this.form.end <= this.form.start) {
          this.form.end = this.dateAdd(this.form.start, minBookingTime)
        }
        this.form.minEnd = this.dateAdd(this.form.start, minBookingTime)
      }

      if (field === 'end') {
        this.form.end = this.ceilTime(this.form.end, minBookingTimeSteps)
      }

      this.validatePeriod()
      this.vehicleAvailability(this.form.start, this.form.end)
    },
    validatePeriod() {
      this.form.errorMessages = {}
      const diff = this.dateDiff(this.form.start, this.form.end)

      if (!this.isOngoing && this.form.start < this.nowIsoLocal) {
        this.form.errorMessages.startInPast = 'Starttidspunkt kan ikke være i fortiden.'
      }

      if (this.isOngoing && this.form.end <= this.toLocalInput(this.booking.end)) {
        this.form.errorMessages.extendOnly = 'Du kan kun forlænge bookingen (nyt sluttidspunkt skal være senere).'
      }

      if (diff < minBookingTime) {
        this.form.errorMessages.startEndDiff = diff < 0
          ? 'Sluttidspunkt må ikke være før starttidspunkt.'
          : 'Mindste booking er 1 time.'
      } else if (diff > maxBookingTime) {
        this.form.errorMessages.startEndDiff = 'Maksimal booking er 10 døgn.'
      }
    },
    onVehicleChange(e) {
      this.form.selectedVehicle = parseInt(e.target.value, 10)
    },
    vehicleAvailability(start, end) {
      if (!start || !end || Object.keys(this.form.errorMessages).length) {
        this.form.availableVehicles = {}
        return
      }
      const url = process.env.VUE_APP_API_ROOT
        + '/vehicle-availability.php?start=' + encodeURIComponent(start)
        + '&end=' + encodeURIComponent(end)
        + '&excludeBookingId=' + this.bookingId

      axios.get(url).then((response) => {
        this.form.availableVehicles = response.data || {}
      })
    },
    bookingUpdate() {
      if (!this.canSubmit) {
        return
      }
      this.submitting = true
      this.global.error.error = false

      const url = process.env.VUE_APP_API_ROOT
        + '/booking.php?mode=update'
        + '&id=' + this.bookingId
        + '&start=' + encodeURIComponent(this.form.start)
        + '&end=' + encodeURIComponent(this.form.end)
        + '&vehicle=' + this.form.selectedVehicle

      axios.get(url)
        .then((response) => {
          const data = response.data
          if (data && data.error) {
            this.global.error.error = true
            this.global.error.code = data.errorCode
            this.global.error.alias = data.alias
            if (data.errorCode === 100) {
              this.vehicleAvailability(this.form.start, this.form.end)
            }
            this.submitting = false
            return
          }
          this.$router.push('/booking/' + this.bookingId + '?updated=1')
        })
        .catch((error) => {
          console.error('Opdatering af booking fejlede:', error)
          this.submitting = false
        })
    },
    ceilTime(dateString, minutesToRound) {
      const [dateStr, timeStr] = dateString.split('T')
      const [hoursStr, minutesStr] = timeStr.split(':')
      const minutes = parseInt(minutesStr, 10)
      let rounded = Math.ceil(minutes / minutesToRound) * minutesToRound
      let hours = parseInt(hoursStr, 10)
      if (rounded === 60) {
        rounded = 0
        hours += 1
      }
      return dateStr + 'T' + ('' + hours).padStart(2, '0') + ':' + ('' + rounded).padStart(2, '0')
    },
    dateAdd(dateStr, minutes) {
      const dateIn = new Date(dateStr)
      const dateOut = new Date(dateIn.getTime() + minutes * 60000)
      return new Date(dateOut.getTime() - dateOut.getTimezoneOffset() * 60000).toISOString().substr(0, 16)
    },
    dateDiff(dateStartStr, dateEndStr) {
      const dateStart = new Date(dateStartStr)
      const dateEnd = new Date(dateEndStr)
      const diff = (dateEnd - dateStart)

      const diffDays = Math.floor(diff / 86400000)
      const diffHrs = Math.floor((diff % 86400000) / 3600000)
      const diffMins = Math.round(((diff % 86400000) % 3600000) / 60000)

      return diffDays * 1440 + diffHrs * 60 + diffMins
    }
  }
})
</script>
